import React, {useMemo, useState} from 'react'

import {useOrderAction} from './Order'
import {SelectDueAction, useSelectDueAction} from './SelectDue'
import {ActionComponentProps, ActionConfig, ActionProps, ActionPropsByType, ActionType, UseActionHook} from './types'

/**
 * Функция-заглушка для экшенов с отсутствующим хуком, чтобы иизбежать conditional-вызовы react-хуков
 * @returns
 */
const actionHookDefault = () => ({triggerAction: void 0})

export const mapTypeToAction: {
  [key in ActionType]: [React.FC<Extract<ActionProps, {type: key}>> | null, UseActionHook | undefined]
} = {
  [ActionType.ORDER]: [null, useOrderAction],
  [ActionType.RESET_DUE]: [null, void 0],
  [ActionType.SELECT_DUE]: [SelectDueAction, useSelectDueAction],
}

export const useAction = <T extends ActionConfig>(
  props?: T,
  actionComponentProps?: Pick<ActionComponentProps, 'onChange'>,
): {ActionComponent: React.ReactElement | null; actionFns: ReturnType<UseActionHook>} => {
  const [isActive, setActive] = useState(false)

  const [ActionWidget, useActionFns] = useMemo(() => {
    if (!props?.type) {
      throw new Error('Action type is not found')
    }

    // TS не смог понять, что к конкретному *типу* экшена относится конкретный *конфиг* экшена,
    // из-за чего приходится применять явное приведение типов
    const [Component, hook = actionHookDefault] = mapTypeToAction[props.type] as [
      React.FC<ActionPropsByType<ActionConfig['type']>> | null,
      UseActionHook,
    ]

    return [Component, hook]
  }, [props?.type])
  const actionFns = useActionFns(isActive, setActive)

  return {
    ActionComponent: props
      ? ActionWidget && <ActionWidget {...props} {...actionComponentProps} isActive={isActive} setActive={setActive} />
      : null,
    actionFns: {triggerAction: actionFns?.triggerAction},
  }
}
